import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import dayjs from "dayjs";
import { Timestamp, doc, setDoc } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";

const defaultState = {
  year: dayjs().year(),
  month: dayjs().month() + 1,
  openingBalance: "",
  grossPerformance: "",
  commissionPercent: "30",
  profitToUser: "",
  netBalanceUser: "",
  note: "seeded via monthly-seed-tool",
  userId: "",
  email: "",
  userName: "",
  mode: "preview",
  confirmText: "",
};

const buildDocId = (userId, year, month) => `${userId}_${year}${String(month).padStart(2, "0")}01120000`;

const MonthlySeedTool = () => {
  const [form, setForm] = useState(defaultState);
  const [preview, setPreview] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const buildPreview = (params) => {
    const year = Number(params.year);
    const month = Number(params.month);
    const openingBalance = Number(params.openingBalance);
    const grossPercentage = Number(params.grossPerformance);
    const fidduxPercent = Number(params.commissionPercent);
    const totalGain = (openingBalance * grossPercentage) / 100;
    const profitToUser =
      params.profitToUser === "" ? totalGain * (1 - fidduxPercent / 100) : Number(params.profitToUser);
    const fidduxCommissionAmount = totalGain - profitToUser;
    const closingBalance = openingBalance + profitToUser;
    const netBalanceUser =
      params.netBalanceUser === "" ? closingBalance : Number(params.netBalanceUser);

    const docId = buildDocId(params.userId, year, month);
    const now = new Date();
    const endDate = new Date(Date.UTC(year, month, 0, 23, 59, 59));
    const graphMonthDate = new Date(Date.UTC(year, month - 1, 0, 23, 0, 0));

    return {
      docId,
      derived: {
        openingBalance,
        grossPercentage,
        fidduxPercent,
        totalGain,
        profitToUser,
        fidduxCommissionAmount,
        closingBalance,
        netBalanceUser,
      },
      monthlyClosingDoc: {
        doc_id: docId,
        user_id: params.userId,
        email: params.email,
        performance_percentage: grossPercentage,
        profit_to_user: profitToUser,
        fiddux_commission: fidduxCommissionAmount,
        fiddux_commission_percentage: fidduxPercent,
        net_balance_after_month: closingBalance,
        created_at: Timestamp.fromDate(now),
        month,
        year,
      },
      monthlyGraphDoc: {
        doc_id: docId,
        user_id: params.userId,
        email: params.email,
        user_name: params.userName,
        graph_month: month - 1,
        graph_year: year,
        graph_month_date: Timestamp.fromDate(graphMonthDate),
        current_month_date: Timestamp.fromDate(graphMonthDate),
        start_date: Timestamp.fromDate(new Date(Date.UTC(year, month - 1, 1, 0, 0, 0))),
        end_date: Timestamp.fromDate(endDate),
        gross_performance_percentage: grossPercentage,
        percentage_after_commission: grossPercentage * (1 - fidduxPercent / 100),
        fiddux_commission: fidduxCommissionAmount,
        fiddux_commission_percentage: fidduxPercent,
        profit_to_user: profitToUser,
        amount_before_performance: openingBalance,
        balance_after_commission: closingBalance,
        total_Monthly_percantage: grossPercentage,
        gross_performance_balance: openingBalance + totalGain,
        total_Monthly_Profit_Loss: profitToUser + fidduxCommissionAmount,
        graph_points: [],
        isNotify: false,
        note: params.note,
      },
      transectionRequestDoc: {
        doc_id: docId,
        user_id: params.userId,
        email: params.email,
        request_type: 2,
        request_status: 5,
        total_amount: profitToUser,
        fiddux_commission: fidduxCommissionAmount,
        fiddux_commission_percentage: fidduxPercent,
        profit_loss: profitToUser,
        month,
        year,
        date: Timestamp.fromDate(endDate),
        created_at: Timestamp.fromDate(now),
      },
      ledgerDoc: {
        doc_id: docId,
        user_id: params.userId,
        transection_type: 3,
        profit_loss_balance: profitToUser,
        profit_loss_percent: grossPercentage,
        previous_balance: openingBalance,
        current_balance: closingBalance,
        new_txn_amount: profitToUser,
        created_at: Timestamp.fromDate(now),
        date: Timestamp.fromDate(endDate),
      },
      userUpdate: {
        net_balance: netBalanceUser,
      },
    };
  };

  const handleSubmit = async () => {
    if (!form.userId || !form.email || !form.userName) {
      setSnackbar({ open: true, message: "Preencha userId, email e nome.", severity: "warning" });
      return;
    }

    try {
      setLoading(true);
      const previewData = buildPreview(form);
      setPreview(previewData);

      if (form.mode === "apply") {
        if (form.confirmText !== "CONFIRMAR") {
          setSnackbar({ open: true, message: "Digite CONFIRMAR para gravar.", severity: "error" });
          return;
        }

        await Promise.all([
          setDoc(doc(DATABASE, "monthly_closings", previewData.docId), previewData.monthlyClosingDoc),
          setDoc(doc(DATABASE, "monthly_grpahs", previewData.docId), previewData.monthlyGraphDoc),
          setDoc(doc(DATABASE, "transection_requests", previewData.docId), previewData.transectionRequestDoc),
          setDoc(doc(DATABASE, "ledgers", previewData.docId), previewData.ledgerDoc),
          setDoc(doc(DATABASE, "users", form.userId), previewData.userUpdate, { merge: true }),
        ]);

        setSnackbar({ open: true, message: "Documentos gravados com sucesso!", severity: "success" });
      } else {
        setSnackbar({ open: true, message: "Preview gerada. Confira os dados abaixo.", severity: "info" });
      }
    } catch (error) {
      console.error("MonthlySeedTool error", error);
      setSnackbar({ open: true, message: "Erro ao processar. Veja o console.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Monthly Data Seed Tool
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Preencha com os valores oficiais de um mês (saldo inicial, percentual, lucro). Use Preview para validar e Apply para gravar.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Ano"
                name="year"
                type="number"
                value={form.year}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Mês"
                name="month"
                type="number"
                value={form.month}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Saldo inicial"
                name="openingBalance"
                type="number"
                value={form.openingBalance}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Performance bruta (%)"
                name="grossPerformance"
                type="number"
                value={form.grossPerformance}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Comissão Fiddux (%)"
                name="commissionPercent"
                type="number"
                value={form.commissionPercent}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Lucro para o cliente (USDT)"
                name="profitToUser"
                type="number"
                value={form.profitToUser}
                onChange={handleChange}
                helperText="Opcional: deixe vazio para cálculo automático"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Saldo final do usuário"
                name="netBalanceUser"
                type="number"
                value={form.netBalanceUser}
                onChange={handleChange}
                helperText="Opcional: saldo final após o mês"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Observação"
                name="note"
                value={form.note}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="User ID"
                name="userId"
                value={form.userId}
                onChange={handleChange}
                placeholder="gq394E7wCAZPr3gdsJ9NVS2sRuP2"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nome"
                name="userName"
                value={form.userName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ToggleButtonGroup value={form.mode} exclusive onChange={(_, newMode) => newMode && setForm((prev) => ({ ...prev, mode: newMode }))}>
                <ToggleButton value="preview">Preview</ToggleButton>
                <ToggleButton value="apply">Apply</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Confirmação"
                name="confirmText"
                value={form.confirmText}
                onChange={handleChange}
                placeholder="Digite CONFIRMAR para gravar"
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" disabled={loading} onClick={handleSubmit}>
                {form.mode === "apply" ? "Aplicar" : "Gerar Preview"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {preview && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Preview / Resultado
            </Typography>
            <pre style={{ whiteSpace: "pre-wrap" }}>
              {JSON.stringify(preview, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MonthlySeedTool;
